<template>
  <div>
    <div
      role="tablist"
      class="grid grid-rows-2 auto-cols-auto justify-items-center justify-start items-center relative"
      :class="tabListClasses"
    >
      <!-- do not make this a Poppins button. It needs to look and act differently from a Poppins button! -->
      <button
        v-for="(tab, index) in tabs"
        :id="`tab-${index}`"
        :key="index"
        role="tab"
        :class="[tabButtonClasses, activeTab === index && 'active text-primary']"
        class="mx-16 primary-focus"
        :style="{ gridRow: '1' }"
        @click="(e) => changeTab(e, index)"
      >
        <div class="flex items-center">
          <span>{{ tab.title }}</span>
          <span v-if="tab.parenthesesNumber >= 0" class="ml-4">({{ tab.parenthesesNumber }})</span>
          <Badge v-if="tab.badgeNumber > 0" class="ml-4" :badgeContent="tab.badgeNumber" />
        </div>
      </button>
      <div v-if="showAddTabAction && !isAtMaxTabs" class="absolute right-16 top-0">
        <slot name="add-tab">
          <!-- default add tab styling, this can be replaced by slot -->
          <Button :value="addTabLabel" variant="text" class="add-tab-btn !p-0" @click="addTab" />
        </slot>
      </div>
      <hr
        id="animatedLine"
        ref="animatedLine"
        class="w-full origin-left border-0 p-0 col-span-1 bg-primary h-4"
        :style="`
          grid-column-start: ${activeTab + 1}; 
          grid-row: ${2}; 
        `"
      />
    </div>
    <HorizontalDivider class="-mt-[10px]" />
    <div v-for="(tab, index) in tabs" v-show="activeTab === index" :key="tab.title">
      <component
        :is="tab.component"
        :id="`panel-${index}`"
        :key="tab.key"
        :alert="alert"
        v-bind="tab.props"
        v-on="tab.listeners || {}"
        @handleAlert="handleAlert"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Badge from '../badge/Badge.vue';
import HorizontalDivider from '../divider/HorizontalDivider.vue';
import Button from '../button/Button.vue';

export default {
  name: 'Tabs',
  components: { Badge, HorizontalDivider, Button },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    tabs: {
      type: Array<{
        title: string;
        badgeNumber: number;
        parenthesesNumber: number;
        component: () => {};
        props?: Record<string, any>;
        listeners?: Record<string, any>;
        key?: string | number;
      }>,
      required: false,
      default: () => [],
    },
    tabListClasses: {
      type: Array,
      default: () => [],
    },
    tabButtonClasses: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: Number,
      default: 0,
      required: false,
    },
    alert: {
      type: Object,
      required: false,
      default: () => {},
    },
    showAddTabAction: {
      type: Boolean,
      default: false,
    },
    addTabLabel: {
      type: String,
      default: 'Add tab',
    },
    maxTabs: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      activeTab: this.modelValue,
      tabCount: this.tabs?.length,
    };
  },
  computed: {
    isAtMaxTabs() {
      return this.tabs.length >= this.maxTabs;
    },
  },
  watch: {
    modelValue(val) {
      this.activeTab = val;
    },
  },
  methods: {
    changeTab({ currentTarget }, index) {
      // animationLine has to run first so it has access to previous line values
      this.animateLine(currentTarget);
      this.$emit('update:modelValue', index);
      this.activeTab = index;
    },
    animateLine(nextLineElement: HTMLElement) {
      // after a tab is selected, the underline is quickly repositioned to its previous placement, then animated to the current values.
      // if browsers ever support css grid animating between columns using grid-column-start, do that instead because it will be way easier.
      const previousLineElement = this.$refs?.animatedLine as HTMLElement;
      previousLineElement?.classList?.remove('mobile-friendly-transition');

      // set the line to the previous left/width values
      const prevLineLeftPosition = previousLineElement?.offsetLeft - nextLineElement?.offsetLeft;
      const prevLineXScale = (previousLineElement?.clientWidth / nextLineElement?.clientWidth).toFixed(2);

      previousLineElement.style.transform = `translateX(${prevLineLeftPosition}px) scale(${prevLineXScale},1)`;
      previousLineElement.style.webkitTransform = `translateX(${prevLineLeftPosition}px) scale(${prevLineXScale},1)`;

      this.$nextTick(() => {
        setTimeout(() => {
          // this part then animates the line to the correct width and left values after a paint
          // setTimeout is needed so there's a layout change for the animation to occur.
          previousLineElement?.classList?.add('mobile-friendly-transition');
          const nextLineLeftPosition = 0;
          const nextLineXScale = '1';

          previousLineElement.style.transform = `translateX(${nextLineLeftPosition}px) scale(${nextLineXScale},1)`;
          previousLineElement.style.webkitTransform = `translateX(${nextLineLeftPosition}px) scale(${nextLineXScale},1)`;
        }, 0);
      });
    },
    handleAlert(status, message) {
      this.$emit('handleAlert', status, message);
    },
    addTab() {
      this.$emit('add-tab');
    },
  },
};
</script>

<style scoped>
.mobile-friendly-transition {
  -webkit-transition: -webkit-transition 250ms ease-in-out;
  -moz-transition: -moz-transition 250ms ease-in-out;
  -o-transition: -o-transition 250ms ease-in-out;
  transition: transform 250ms ease-in-out;
}
</style>
